<script setup>
import { useLocalStorage } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { computed, shallowReactive, shallowRef, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAiStream } from '@/composables/useAiStream.js';
import { useAuthStore } from '@/stores/authStore.js';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  aiContent: {
    type: [String,null],
    required: true,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  productName: {
    type: String,
    default: '',
  },
  postId: {
    type: String,
    required: true,
  },
  showBuyerActions: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['toggle-expanded', 'update:expanded', 'update:current-content']);
const { t } = useI18n();
const authStore = useAuthStore();
const DAILY_LIMIT = 10;
const PENDING_LIMIT = 5;
const RESET_HOURS = 24;

//const { startStream, cancel, isStreaming } = useEnhanceStream();
const { startStream, cancel, isStreaming } = useAiStream();


// Use shallowReactive for cache to avoid deep reactivity overhead
const enhancedContentCache = shallowReactive({});


// Usage tracking with VueUse
const usageData = useLocalStorage('ai_enhance_usage', {
  count: 0,
  timestamp: Date.now(),
});

// Primitive state - use shallowRef for better performance
const aiEnhanced = shallowRef(false);
// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- initial value only; watcher below syncs subsequent prop changes
const displayContent = shallowRef(props.content);

// Computed properties
const shouldShowExpand = computed(() => props.content.length > 120);

// Direct cache lookup - no computed needed, reduces reactivity chain
const getCachedContent = () => enhancedContentCache[props.postId] || props.aiContent;

// Reset usage counter when the 24h window has elapsed.
// Kept as a watch effect so `isLimitExceeded` below remains a pure computed.
watchEffect(() => {
  const hoursPassed = (Date.now() - usageData.value.timestamp) / (1000 * 60 * 60);
  if (hoursPassed >= RESET_HOURS) {
    usageData.value = { count: 0, timestamp: Date.now() };
  }
});

const effectiveLimit = computed(() =>
  authStore.isMembershipPending ? PENDING_LIMIT : DAILY_LIMIT,
);

const isLimitExceeded = computed(() => {
  const hoursPassed = (Date.now() - usageData.value.timestamp) / (1000 * 60 * 60);
  if (hoursPassed >= RESET_HOURS) return false;
  return usageData.value.count >= effectiveLimit.value;
});

const remainingUses = computed(() => {
  if (isLimitExceeded.value) return 0;
  return Math.max(0, effectiveLimit.value - usageData.value.count);
});

const timeUntilReset = computed(() => {
  const hoursPassed = (Date.now() - usageData.value.timestamp) / (1000 * 60 * 60);
  const hoursLeft = Math.ceil(RESET_HOURS - hoursPassed);
  return `${hoursLeft} ساعة`;
});

// Badge color based on remaining uses
const badgeColor = computed(() => {
  if (isLimitExceeded.value) return 'red';
  if (remainingUses.value <= 3) return 'orange';
  return 'green';
});

// Single source of truth for current content
const currentContent = computed(() => {
  return aiEnhanced.value ? displayContent.value : props.content;
});

// Methods
const showToast = (text) => {

  f7?.toast?.create({
    text,
    position: 'center',
    closeTimeout: 3000,
  }).open();

};

const handleAiEnhance = async () => {
  if (isStreaming.value) {
    cancel();
    return;
  }

  if (aiEnhanced.value) {
    // Reverting to original
    displayContent.value = props.content;
    aiEnhanced.value = false;
    return;
  }

  if (isLimitExceeded.value) {
    showToast(
      authStore.isMembershipPending
        ? t('product.pendingMembershipAi')
        : `تم استنفاد المحاولات اليومية. تقدر تحاول تاني بعد ${timeUntilReset.value}`,
    );
    return;
  }

  // Check cache directly - no reactive lookup
  const cachedContent = getCachedContent();
  if (cachedContent) {
    displayContent.value = cachedContent;
    aiEnhanced.value = true;
    if (!props.expanded) {
      emit('update:expanded', true);
    }
    return;
  }

  try {
    if (!props.expanded) {
      emit('update:expanded', true);
    }

    await startStream(displayContent, props.postId);

    // Cache the result - plain string assignment, no deep reactivity
    enhancedContentCache[props.postId] = displayContent.value;
    aiEnhanced.value = true;

    // Update usage count
    usageData.value = {
      count: usageData.value.count + 1,
      timestamp: usageData.value.timestamp,
    };
  } catch (err) {
    console.error('AI enhancement failedss:', err);
    displayContent.value = props.content;
    aiEnhanced.value = false;

    if (err.name !== 'AbortError') {
      showToast('فشل تحسين المحتوى. حاول تاني');
    }
  }
};

// Efficient watcher - only watches the computed value, not intermediate refs
watch(currentContent, (newContent) => {
  emit('update:current-content', newContent);
}, { immediate: true });

// Watch for external content changes only when not streaming/enhanced
watch(() => props.content, (newContent) => {
  if (!isStreaming.value && !aiEnhanced.value) {
    displayContent.value = newContent;
  }
});
</script>

<template>
  <f7-card-content class="post-content">
    <div class="content-header">
      <div v-if="productName" class="product-name text-secondary">
        {{ productName }}
      </div>
      <f7-button
        v-if="showBuyerActions"
        round
        small
        class="ai-enhance-btn"
        :class="{
          enhanced: aiEnhanced,
          streaming: isStreaming
        }"
        @click="handleAiEnhance"
      >
        <f7-icon
          ios="f7:sparkles"
          md="material:auto_awesome"
          size="14"
          :class="{ spinning: isStreaming }"
        >
          <f7-badge v-if="!isStreaming && !aiEnhanced" :color="badgeColor">
            {{ remainingUses }}
          </f7-badge>
        </f7-icon>
        <span class="ai-text">


          {{ isStreaming ? $t("product.enhancing"): aiEnhanced ? t("product.revertEnhance") : t("product.enhance") }}
        </span>
      </f7-button>
    </div>

    <div
      class="content-text"
      :class="{
        'ai-enhanced': aiEnhanced,
        'line-clamp-3': !expanded && shouldShowExpand
      }"
      dir="rtl"
    >
      {{ currentContent }}
    </div>

    <f7-link
      v-if="shouldShowExpand"
      class="read-more-link text-color-primary"
      @click="emit('toggle-expanded')"
    >
      <span>{{ expanded ? 'عرض أقل' : 'عرض المزيد' }}</span>
      <f7-icon
        f7="chevron_down"
        size="14"
        class="chevron-icon"
        :class="{ rotated: expanded }"
      ></f7-icon>
    </f7-link>
  </f7-card-content>
</template>

<style scoped>
.post-content {
  padding: var(--poolit-spacing-md);
}

.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--poolit-spacing-sm);
  margin-bottom: var(--poolit-spacing-sm);
}

.product-name {
  font-size: 1rem;
  font-weight: var(--poolit-font-weight-semibold);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-enhance-btn {
  --f7-button-height: 32px;
  --f7-button-padding-horizontal: var(--poolit-spacing-sm);
  --f7-button-bg-color: rgba(var(--poolit-primary-rgb), 0.1);
  --f7-button-text-color: var(--poolit-primary-500);
  --f7-button-border-color: rgba(var(--poolit-primary-rgb), 0.3);
  --f7-button-border-width: 1.5px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--poolit-font-size-small);
  font-weight: var(--poolit-font-weight-medium);
  transition: all var(--poolit-transition-normal) var(--poolit-transition-ease);
  flex-shrink: 0;
}

.ai-enhance-btn:active {
  transform: scale(0.95);
}

.ai-enhance-btn.enhanced {
  --f7-button-bg-color: var(--poolit-primary-500);
  --f7-button-text-color: var(--poolit-text-on-primary);
  --f7-button-border-color: var(--poolit-primary-600);
  box-shadow: 0 4px 12px rgba(var(--poolit-primary-rgb), 0.3);
}

.ai-enhance-btn.streaming {
  pointer-events: none;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.ai-text {
  font-size: 11px;
  font-weight: var(--poolit-font-weight-semibold);
}

.content-text {
  font-size: var(--poolit-font-size-body);
  line-height: var(--poolit-line-height-body);
  color: var(--poolit-text-secondary);
  margin-bottom: var(--poolit-spacing-sm);
  white-space: pre-line;
  word-wrap: break-word;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.content-text.ai-enhanced {
  padding: var(--poolit-spacing-sm);
  background: linear-gradient(135deg,
  rgba(var(--poolit-primary-rgb), 0.05),
  rgba(var(--poolit-primary-rgb), 0.08));
  border-right: 3px solid var(--poolit-primary-500);
  border-radius: var(--poolit-radius-md);
  margin-bottom: var(--poolit-spacing-sm);
  position: relative;
}

.content-text.ai-enhanced::before {
  content: '✨ محتوى محسّن بالذكاء الاصطناعي';
  display: block;
  font-size: 11px;
  font-weight: var(--poolit-font-weight-semibold);
  color: var(--poolit-primary-600);
  margin-bottom: 6px;
  opacity: 0.9;
}

.read-more-link {
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-medium);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.chevron-icon {
  transition: transform var(--poolit-transition-fast);
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

@media (max-width: 480px) {
  .post-content {
    padding: var(--poolit-spacing-sm);
  }

  .product-name {
    font-size: 0.9375rem;
  }
}

.dark .content-text {
  color: var(--poolit-text-secondary-dark);
}
</style>
